import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldAlert, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { api } from './api';

const FLAGS = [
  { group: 'Red flags (urgent)', items: [
    { id: 'high_fever',         label: 'High fever (>39°C / >102°F)' },
    { id: 'trouble_breathing',  label: 'Trouble breathing' },
    { id: 'very_lethargic',     label: 'Very lethargic / hard to wake' },
    { id: 'blue_lips',          label: 'Blue lips or fingertips' },
    { id: 'seizure',            label: 'Seizure or unresponsive' },
    { id: 'dehydration_signs',  label: 'Dehydration signs (dry mouth, no tears, no urine)' },
    { id: 'stiff_neck',         label: 'Stiff neck' },
    { id: 'rash_spreading',     label: 'Spreading rash' },
    { id: 'persistent_vomit',   label: 'Persistent vomiting' },
  ] },
  { group: 'Mild signs', items: [
    { id: 'mild_fever',     label: 'Mild fever' },
    { id: 'cough_no_fever', label: 'Cough without fever' },
    { id: 'loose_stools',   label: 'Loose stools' },
    { id: 'irritable_only', label: 'Irritable only' },
    { id: 'appetite_dip',   label: 'Appetite dip' },
    { id: 'mild_rash',      label: 'Mild rash, not spreading' },
  ] },
];

const COLORS = {
  rose:    { border: 'border-rose-300',    bg: 'bg-rose-50',    text: 'text-rose-700',    dot: 'bg-rose-500',    icon: AlertTriangle },
  amber:   { border: 'border-amber-300',   bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500',   icon: AlertTriangle },
  emerald: { border: 'border-emerald-300', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', icon: CheckCircle2 },
};

const QuickCheck = () => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.quickCheck({ flags: selected });
      setResult(r);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50/50 via-white to-white pb-24" data-testid="child-quick-check">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <button onClick={() => navigate('/childrens-health')} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-4">
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <header className="mb-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-red-500 flex items-center justify-center">
              <ShieldAlert className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Something feels off?</h1>
              <p className="text-xs text-gray-500 mt-0.5">A quick urgency triage. Tick anything you've noticed.</p>
            </div>
          </div>
        </header>

        {!result && (
          <>
            {FLAGS.map((g) => (
              <div key={g.group} className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5 mb-3">
                <h3 className="text-sm font-bold text-gray-900 mb-3">{g.group}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {g.items.map((it) => {
                    const active = selected.includes(it.id);
                    return (
                      <button
                        key={it.id}
                        type="button"
                        onClick={() => toggle(it.id)}
                        className={`text-left text-sm font-medium rounded-xl border-2 px-3 py-3 transition-all ${
                          active ? 'border-rose-500 bg-rose-50 text-rose-900' : 'border-gray-200 hover:border-rose-300 text-gray-700'
                        }`}
                        data-testid={`quick-flag-${it.id}`}
                      >
                        {active && <CheckCircle2 className="h-4 w-4 inline mr-1.5 text-rose-600" />}
                        {it.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            <div className="flex justify-end">
              <button
                onClick={submit}
                disabled={submitting}
                className="inline-flex items-center gap-2 rounded-xl px-5 py-3 font-semibold shadow-sm bg-gradient-to-br from-rose-500 to-red-500 hover:brightness-110 text-white transition-all"
                data-testid="quick-submit"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Get assessment →
              </button>
            </div>
          </>
        )}

        {result && (() => {
          const C = COLORS[result.color] || COLORS.amber;
          const Ci = C.icon;
          return (
            <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300" data-testid="quick-result">
              <div className={`rounded-2xl border-2 ${C.border} ${C.bg} p-5`}>
                <div className="flex items-start gap-3">
                  <Ci className={`h-6 w-6 ${C.text} shrink-0 mt-0.5`} />
                  <div>
                    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-1">Assessment</p>
                    <h2 className={`text-2xl font-bold ${C.text}`} data-testid="quick-result-title">{result.title}</h2>
                    <p className="text-sm text-gray-800 mt-2 leading-relaxed" data-testid="quick-result-message">{result.message}</p>
                  </div>
                </div>
              </div>

              <p className="text-[11px] text-gray-400 text-center">
                HealthLoop supports awareness — it does not diagnose or replace pediatric care.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  onClick={() => { setResult(null); setSelected([]); }}
                  className="bg-white hover:bg-gray-50 text-gray-900 font-semibold rounded-xl px-5 py-3 border border-gray-200 transition-all"
                  data-testid="quick-restart"
                >
                  Run again
                </button>
                <button
                  onClick={() => navigate('/childrens-health')}
                  className="bg-gradient-to-br from-rose-500 to-red-500 hover:brightness-110 text-white font-semibold rounded-xl px-5 py-3 shadow-sm transition-all"
                  data-testid="quick-back-hub"
                >
                  Back to hub
                </button>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default QuickCheck;
